import { onMounted, onUnmounted, ref } from 'vue'

const KEYBOARD_HEIGHT_THRESHOLD_PX = 50
const SCROLL_DELAY_MS = 300
/** iOS form accessory (keyboard toolbar) is not always excluded from `visualViewport.height`. */
const IOS_ACCESSORY_BAR_PX = 48
const BOTTOM_SAFE_MARGIN_PX = 8
const TOP_SAFE_MARGIN_PX = 16

function isTextInput(
  el: EventTarget | null,
): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el || !(el instanceof HTMLElement)) return false
  if (el instanceof HTMLTextAreaElement) return true
  if (el instanceof HTMLInputElement) {
    const type = el.type
    return (
      type === 'text' ||
      type === 'search' ||
      type === 'email' ||
      type === 'tel' ||
      type === 'url' ||
      type === 'password' ||
      type === 'number' ||
      type === ''
    )
  }
  return false
}

function keyboardLikelyOpen(): boolean {
  if (typeof window === 'undefined' || !window.visualViewport) return false
  return window.innerHeight - window.visualViewport.height > KEYBOARD_HEIGHT_THRESHOLD_PX
}

function measureSafeBottomObstructions(): number {
  if (typeof document === 'undefined') return 0
  const nodes = document.querySelectorAll<HTMLElement>('[data-keyboard-safe-bottom]')
  let sum = 0
  nodes.forEach((node) => {
    const { height, width } = node.getBoundingClientRect()
    if (height > 0 && width > 0) {
      sum += height
    }
  })
  return sum
}

function findScrollableAncestor(el: HTMLElement): HTMLElement | typeof window {
  let current: HTMLElement | null = el.parentElement
  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    const yScrollable =
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    if (yScrollable && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return window
}

function scrollFocusedIntoSafeArea(
  el: HTMLInputElement | HTMLTextAreaElement,
): void {
  if (typeof window === 'undefined' || !window.visualViewport) {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    return
  }
  const vv = window.visualViewport
  const safeBottomObstructionPx = measureSafeBottomObstructions()
  const usableTop = vv.offsetTop + TOP_SAFE_MARGIN_PX
  const usableBottom =
    vv.offsetTop +
    vv.height -
    IOS_ACCESSORY_BAR_PX -
    safeBottomObstructionPx -
    BOTTOM_SAFE_MARGIN_PX

  if (usableBottom <= usableTop) {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    return
  }

  const rect = el.getBoundingClientRect()
  if (rect.top >= usableTop && rect.bottom <= usableBottom) {
    return
  }

  const elCenter = (rect.top + rect.bottom) / 2
  const usableCenter = (usableTop + usableBottom) / 2
  const delta = elCenter - usableCenter
  const scroller = findScrollableAncestor(el)
  if (scroller === window) {
    window.scrollBy({ top: delta, behavior: 'smooth' })
  } else {
    scroller.scrollBy({ top: delta, behavior: 'smooth' })
  }
}

export default function useKeyboardScrollFix() {
  const focusedField = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)
  let lastVisualHeight = 0
  let scrollTimeoutId: ReturnType<typeof setTimeout> | null = null

  function clearScrollTimeout() {
    if (scrollTimeoutId !== null) {
      clearTimeout(scrollTimeoutId)
      scrollTimeoutId = null
    }
  }

  function scheduleScrollIntoView() {
    const el = focusedField.value
    if (!el || typeof document === 'undefined') return

    clearScrollTimeout()
    scrollTimeoutId = setTimeout(() => {
      scrollTimeoutId = null
      if (document.activeElement !== el) return
      scrollFocusedIntoSafeArea(el)
    }, SCROLL_DELAY_MS)
  }

  function onFocusIn(event: FocusEvent) {
    const target = event.target
    if (!isTextInput(target)) return
    focusedField.value = target

    if (keyboardLikelyOpen()) {
      scheduleScrollIntoView()
    }
  }

  function onFocusOut() {
    focusedField.value = null
  }

  function onVisualViewportResize() {
    if (typeof window === 'undefined' || !window.visualViewport) return

    const h = window.visualViewport.height
    const shrank =
      lastVisualHeight > 0 && lastVisualHeight - h > KEYBOARD_HEIGHT_THRESHOLD_PX
    lastVisualHeight = h

    if (shrank && focusedField.value) {
      scheduleScrollIntoView()
    }
  }

  onMounted(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return

    lastVisualHeight = window.visualViewport.height

    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    window.visualViewport.addEventListener('resize', onVisualViewportResize)
  })

  onUnmounted(() => {
    clearScrollTimeout()
    if (typeof window === 'undefined' || !window.visualViewport) return

    document.removeEventListener('focusin', onFocusIn)
    document.removeEventListener('focusout', onFocusOut)
    window.visualViewport.removeEventListener('resize', onVisualViewportResize)
  })
}
